import React, { useState, useMemo } from 'react';
import type { InstrumentType } from '../../types/composition';
import type { InstrumentParameters } from '../../types/instruments';
import type { EffectInstance } from '../../types/effects';
import { InstrumentSelectorPanel } from './InstrumentSelectorPanel';
import { InstrumentParameters as InstrumentParametersComponent } from '../grid/InstrumentParameters';
import { EffectsPanel } from '../grid/EffectsPanel';
import { instrumentDatabase } from '../../services/instruments/InstrumentDatabase';
import { CloseButton } from '../common/CloseButton';
import './SoundPanel.css';

/**
 * Props for SoundPanel component
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7
 */
export interface SoundPanelProps {
  /** Track index for the selected track */
  trackIndex: number;
  /** Instrument type for the selected track */
  instrument: InstrumentType;
  /** Current instrument parameters */
  parameters: InstrumentParameters;
  /** Current effects chain */
  effectsChain: EffectInstance[];
  /** Whether custom parameters are enabled */
  parametersEnabled: boolean;
  /** Callback when instrument changes */
  onInstrumentChange: (instrument: InstrumentType) => void;
  /** Callback when parameters change */
  onParametersChange: (parameters: InstrumentParameters) => void;
  /** Callback when parameters enabled state changes */
  onParametersEnabledChange: (enabled: boolean) => void;
  /** Callback when effects chain changes */
  onEffectsChange: (effects: EffectInstance[]) => void;
  /** Callback when panel is closed */
  onClose: () => void;
}

/**
 * SoundPanel Component
 * 
 * Full-screen panel for editing synth parameters and effects for the selected track.
 * Reuses existing InstrumentParameters and EffectsPanel components.
 * Displays controls for selected track only.
 * Provides close button in header.
 * Saves parameter changes when panel is closed.
 * 
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5, 8.6, 8.7
 */
export const SoundPanel: React.FC<SoundPanelProps> = ({
  trackIndex,
  instrument,
  parameters,
  effectsChain,
  parametersEnabled,
  onInstrumentChange,
  onParametersChange,
  onParametersEnabledChange,
  onEffectsChange,
  onClose,
}) => {
  // State for instrument selector panel
  const [isInstrumentSelectorOpen, setIsInstrumentSelectorOpen] = useState(false);

  // Get instrument display name from database
  const instrumentDisplayName = useMemo(() => {
    const metadata = instrumentDatabase.getInstrument(instrument);
    return metadata?.displayName || instrument;
  }, [instrument]);

  /**
   * Handle effects panel toggle
   * The EffectsPanel component expects an onToggle callback, but we always keep it expanded
   */
  const handleEffectsToggle = () => {
    // Always expanded in mobile view
  };

  /**
   * Handle instrument selector open
   */
  const handleOpenInstrumentSelector = () => {
    setIsInstrumentSelectorOpen(true);
  };

  /**
   * Handle instrument selector close
   */
  const handleCloseInstrumentSelector = () => {
    setIsInstrumentSelectorOpen(false);
  };

  /**
   * Handle instrument selection
   */
  const handleInstrumentSelect = (newInstrument: InstrumentType) => {
    onInstrumentChange(newInstrument);
    setIsInstrumentSelectorOpen(false);
  };

  return (
    <>
      {/* Backdrop overlay - Requirement 8.1: Full-screen overlay */}
      <div 
        className="sound-panel-backdrop" 
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Panel container - Requirement 8.1: Full-screen panel with z-index: 100 */}
      <div 
        className="sound-panel" 
        role="dialog" 
        aria-label="Sound panel"
        aria-modal="true"
      >
        {/* Header with close button - Requirement 8.6 */}
        <div className="sound-panel-header">
          <div className="sound-panel-title-section">
            <h2 className="sound-panel-title">Sound</h2>
            <p className="sound-panel-subtitle">
              Track {trackIndex + 1}: {instrumentDisplayName}
            </p>
          </div>
          <CloseButton
            onClick={onClose}
            ariaLabel="Close sound panel"
            size="medium"
            variant="default"
          />
        </div>

        {/* Scrollable content area - Requirements 8.2, 8.3, 8.4, 8.5 */}
        <div className="sound-panel-content">
          {/* Instrument Selector Section */}
          <div className="sound-panel-section">
            <h3 className="sound-panel-section-title">
              <i className="fa-solid fa-music" aria-hidden="true"></i>
              Instrument
            </h3>
            <div className="sound-panel-section-content">
              <button
                className="instrument-selector-button"
                onClick={handleOpenInstrumentSelector}
                aria-label="Select instrument"
              >
                <div className="instrument-selector-button-content">
                  <span className="instrument-selector-button-label">Current:</span>
                  <span className="instrument-selector-button-value">
                    {instrumentDisplayName}
                  </span>
                </div>
                <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          {/* Instrument Parameters Section - Requirement 8.3, 8.4, 8.5 */}
          <div className="sound-panel-section">
            <h3 className="sound-panel-section-title">
              <i className="fa-solid fa-sliders" aria-hidden="true"></i>
              Instrument Parameters
            </h3>
            <div className="sound-panel-section-content">
              {/* Reuse existing InstrumentParameters component */}
              <InstrumentParametersComponent
                instrument={instrument}
                parameters={parameters}
                onChange={onParametersChange}
                parametersEnabled={parametersEnabled}
                onParametersEnabledChange={onParametersEnabledChange}
                row={trackIndex}
                isExpanded={true}
              />
            </div>
          </div>

          {/* Effects Section - Requirement 8.4 */}
          <div className="sound-panel-section">
            <h3 className="sound-panel-section-title">
              <i className="fa-solid fa-wand-magic-sparkles" aria-hidden="true"></i>
              Effects Chain
            </h3>
            <div className="sound-panel-section-content">
              {/* Reuse existing EffectsPanel component */}
              <EffectsPanel
                row={trackIndex}
                isExpanded={true}
                onToggle={handleEffectsToggle}
                effectsChain={effectsChain}
                onEffectsChange={onEffectsChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Instrument Selector Panel */}
      {isInstrumentSelectorOpen && (
        <InstrumentSelectorPanel
          currentInstrument={instrument}
          onInstrumentSelect={handleInstrumentSelect}
          onClose={handleCloseInstrumentSelector}
        />
      )}
    </>
  );
};
